import { randomInt } from '../../lib/random';

/**
 * Le compteur de coms, et le classement qui le suit.
 *
 * Un vrai Skyblog affichait toujours un nombre de commentaires en train de
 * monter, sans qu'on sache jamais d'où il venait. Celui-ci part de 4021 et
 * grimpe de 1 à 3 toutes les quatre secondes — et **il n'est pas mémorisé** :
 * il repart de 4021 à chaque visite, ce qui est plus drôle qu'un vrai compteur.
 *
 * Tout est pur ici : le composant ne fait que poser un intervalle sur ces
 * réponses. C'est ce qui permet de vérifier la seule promesse visible du gag —
 * **le classement ne s'inverse jamais** — sans regarder l'écran pendant une
 * heure.
 */

/** Point de départ du compteur, comme dans le handoff. */
export const COMS_START = 4021;

/** Cadence de l'incrément. */
export const COMS_TICK_MS = 4_000;

/** Part du total affichée par le second article : les deux bougent ensemble. */
export const SECOND_ARTICLE_RATIO = 0.38;

/** Les trois kikoos du classement, et leur avance de départ. */
const KIKOOS: readonly (readonly [name: string, base: number])[] = [
  ['jean-mich_92', 412],
  ['maman_de_kevin', 208],
  ['x_LoLiTa_du_57_x', 155],
];

export interface Kikoo {
  readonly name: string;
  readonly coms: number;
}

/** Incrément d'un tour : 1, 2 ou 3 coms. */
export function comsStep(pick: (max: number) => number = randomInt): number {
  return 1 + pick(3);
}

/** Ce qu'affiche la méta du second article. */
export function secondArticleComs(coms: number): number {
  return Math.round(coms * SECOND_ARTICLE_RATIO);
}

/**
 * Le classement.
 *
 * Chaque kikoo gagne `coms / (3 + rang * 2)` : les trois montent donc à des
 * vitesses différentes, mais **l'écart de départ est trop grand pour être
 * rattrapé** — le premier reste premier. Un classement qui s'inverserait
 * donnerait l'impression d'un bug, pas d'un blog.
 */
export function topKikoos(coms: number): readonly Kikoo[] {
  return KIKOOS.map(([name, base], rank) => ({
    name,
    coms: base + Math.floor(Math.max(0, coms) / (3 + rank * 2)),
  }));
}

/** « 4 021 » en français, « 4,021 » en anglais. */
export function formatComs(value: number, lang: 'fr' | 'en'): string {
  try {
    return new Intl.NumberFormat(lang === 'fr' ? 'fr-FR' : 'en-GB').format(value);
  } catch {
    return String(value);
  }
}
